import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Mail, Loader2, Edit3, AlertCircle, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";
import { useTranslation } from "react-i18next";

export default function InvoiceEmailModal({ 
  isOpen, 
  onClose, 
  invoice, 
  companyName = "",
  onEmailSent = null
}) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [customerEmailLoading, setCustomerEmailLoading] = useState(false);
  const [canEditRecipient, setCanEditRecipient] = useState(false);
  const [customerEmailData, setCustomerEmailData] = useState(null);
  const [formData, setFormData] = useState({
    recipient_email: "",
    subject: invoice ? `Rechnung ${invoice.invoice_number}${companyName ? ` von ${companyName}` : ""}` : "",
    body: ""
  });
  const [errors, setErrors] = useState({});

  // Fetch customer email when invoice changes (P0A)
  useEffect(() => {
    if (invoice && invoice.customer_id) {
      fetchCustomerEmail(invoice.customer_id);
    }
  }, [invoice]);

  // Reset form when invoice changes
  useEffect(() => {
    if (invoice) {
      // Personalisierte Anrede: Kundenname wenn verfügbar
      const customerName = invoice.customer_name;
      const greeting = customerName
        ? `Guten Tag ${customerName},`
        : `Sehr geehrte Damen und Herren,`;

      // Betrag formatieren
      const amount = invoice.total != null
        ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: invoice.currency || 'EUR' }).format(invoice.total)
        : '';

      const amountLine = amount ? `\nDer Rechnungsbetrag beläuft sich auf ${amount}.` : '';

      // Grußformel mit Firmenname
      const closingName = companyName || '';
      const closing = closingName
        ? `Mit freundlichen Grüßen\n${closingName}`
        : `Mit freundlichen Grüßen`;

      const defaultBody = `${greeting}

anbei erhalten Sie die Rechnung ${invoice.invoice_number} vom ${new Date(invoice.invoice_date).toLocaleDateString('de-DE')}.${amountLine}

Bei Fragen stehe ich Ihnen gerne zur Verfügung.

${closing}`;

      setFormData({
        recipient_email: customerEmailData?.email || invoice.customer_email || "",
        subject: `Rechnung ${invoice.invoice_number}${companyName ? ` von ${companyName}` : ""}`,
        body: defaultBody
      });
      setErrors({});
      setCanEditRecipient(!customerEmailData?.has_email); // Auto-enable editing if no customer email
    }
  }, [invoice, companyName, customerEmailData]);

  const fetchCustomerEmail = async (customerId) => {
    setCustomerEmailLoading(true);
    try {
      const response = await api.get(`/customers/${customerId}/email`);
      setCustomerEmailData(response.data);
    } catch (error) {
      console.error('Error fetching customer email:', error);
      setCustomerEmailData({ has_email: false, email: null, name: null });
    } finally {
      setCustomerEmailLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!formData.recipient_email.trim()) {
      if (!customerEmailData?.has_email) {
        newErrors.recipient_email = "E-Mail-Adresse ist erforderlich. Für diesen Kunden ist keine E-Mail-Adresse hinterlegt.";
      } else {
        newErrors.recipient_email = "E-Mail-Adresse ist erforderlich";
      }
    } else if (!validateEmail(formData.recipient_email)) {
      newErrors.recipient_email = "Ungültige E-Mail-Adresse";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await api.post('/invoices/send-email', {
        invoice_id: invoice.id,
        recipient_email: formData.recipient_email.trim(),
        subject: formData.subject.trim() || undefined,
        body: formData.body.trim() || undefined
      });
      
      toast.success("Rechnung erfolgreich per E-Mail versendet!");
      if (onEmailSent) {
        onEmailSent(); // Refresh invoices list
      }
      onClose();
    } catch (error) {
      console.error('Error sending invoice email:', error);
      const errorMessage = error.response?.data?.detail || "Fehler beim Versenden der E-Mail";
      toast.error(errorMessage);
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      // Reset form
      setFormData({
        recipient_email: "",
        subject: "",
        body: ""
      });
      setErrors({});
      setCanEditRecipient(false);
      setCustomerEmailData(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            Rechnung per E-Mail senden
          </DialogTitle>
          {invoice && (
            <p className="text-sm text-gray-600 mt-2">
              Rechnung {invoice.invoice_number} an {invoice.customer_name} senden
            </p>
          )}
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{errors.general}</p>
            </div>
          )}
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="recipient_email" className="text-sm font-semibold">
                Empfänger *
              </Label>
              {customerEmailData?.has_email && (
                <div className="flex items-center space-x-2">
                  <Label htmlFor="edit-recipient" className="text-xs text-gray-600">
                    Empfänger ändern
                  </Label>
                  <Switch
                    id="edit-recipient"
                    checked={canEditRecipient}
                    onCheckedChange={setCanEditRecipient}
                    disabled={isLoading}
                  />
                </div>
              )}
            </div>
            
            {customerEmailLoading ? (
              <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-md">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-gray-600">Lade Kundendaten...</span>
              </div>
            ) : (
              <>
                <Input
                  id="recipient_email"
                  type="email"
                  value={formData.recipient_email}
                  onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                  placeholder="kunde@beispiel.de"
                  className={errors.recipient_email ? "border-red-300" : ""}
                  disabled={isLoading || (!canEditRecipient && customerEmailData?.has_email)}
                  readOnly={!canEditRecipient && customerEmailData?.has_email}
                  required
                />
                
                {!customerEmailData?.has_email && (
                  <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">Keine E-Mail-Adresse hinterlegt</p>
                      <p>Für diesen Kunden ist keine E-Mail-Adresse gespeichert. Bitte geben Sie eine E-Mail-Adresse ein.</p>
                    </div>
                  </div>
                )}
                
                {customerEmailData?.has_email && !canEditRecipient && (
                  <div className="flex items-start space-x-2 p-3 bg-green-50 border border-green-200 rounded-md">
                    <Mail className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-green-800">
                      <p className="font-medium">E-Mail aus Kundendatenbank</p>
                      <p>Die E-Mail-Adresse wurde automatisch aus den Kundendaten übernommen.</p>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {errors.recipient_email && (
              <p className="text-sm text-red-600">{errors.recipient_email}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="subject" className="text-sm font-semibold">
              Betreff
            </Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Rechnung [Nummer] von [Firma]"
              disabled={isLoading}
            />
            <p className="text-xs text-gray-500">
              Leer lassen für automatischen Betreff
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="body" className="text-sm font-semibold">
              Nachricht
            </Label>
            <Textarea
              id="body"
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              placeholder={`Sehr geehrte Damen und Herren,

anbei erhalten Sie die Rechnung für unsere Leistungen.

Vielen Dank für Ihr Vertrauen.

Mit freundlichen Grüßen`}
              rows={6}
              disabled={isLoading}
            />
            <p className="text-xs text-gray-500">
              Leer lassen für Standard-Nachricht
            </p>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Was passiert beim Versenden?</p>
                <ul className="space-y-1 text-xs">
                  <li>• Die Rechnung wird als PDF-Datei angehängt</li>
                  <li>• Die E-Mail wird von Ihrem ivoliq-Account versendet</li>
                  <li>• Der Empfänger erhält eine professionelle E-Mail</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Juni 2026 — ZUGFeRD: dezenter Hinweis, dass die PDF bereits eine gültige E-Rechnung ist */}
          {invoice && ["DE", "AT"].includes(invoice.country) && (invoice.currency || "EUR") === "EUR" && (
            <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="einvoice-send-hint">
              <BadgeCheck className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-emerald-800">
                <span className="font-semibold">Bereits eine gültige E-Rechnung:</span> Diese PDF enthält
                das eingebettete Rechnungs-XML (ZUGFeRD, EN 16931) — konform mit der E-Rechnungspflicht ab 2027/2028.
              </p>
            </div>
          )}
          
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1"
            >
              {t("common.cancel", "Abbrechen")}
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Wird gesendet...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  E-Mail senden
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}